import React, { forwardRef } from 'react';
import { Box, Stack, Typography, Button, CircularProgress, Alert } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';

interface DeleteBulkUsersDrawerContentProps {
  isSubmitting: boolean;
  selectedFile: File | null;
  onFileChange: (file: File | null) => void;
  onSubmit: (file: File) => void;
}

const DeleteBulkUsersDrawerContent = forwardRef<HTMLInputElement, DeleteBulkUsersDrawerContentProps>(
  ({ isSubmitting, selectedFile, onFileChange, onSubmit }, fileInputRef) => {

    const handleUploadClick = () => {
      (fileInputRef as React.RefObject<HTMLInputElement>).current?.click();
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0] || null;
      onFileChange(file);
    };

    const handleSubmit = () => {
      if (selectedFile) {
        onSubmit(selectedFile);
      }
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ pb: 4 }}>
          <Alert severity="info" icon={false} sx={{ backgroundColor: '#E5F8FE', color: '#26C6F9' }}>
            <Typography variant="subtitle2">Upload users in bulk, and the system will automatically identify and delete them.</Typography>
          </Alert>
        </Box>
        {/* Upload Box */}
        <Stack spacing={2}>
          <Box
            onClick={handleUploadClick}
            sx={{
              border: '2px dashed #ccc',
              borderRadius: '8px',
              p: 2,
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#f9f9f9' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              {selectedFile ? (
                <>
                  <CloudUploadIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                  <Box>
                    <Typography
                      variant="subtitle1"
                      fontWeight={500}
                      noWrap
                      sx={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        maxWidth: 200
                      }}
                    >
                      {selectedFile.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Click to change file
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  <CloudUploadIcon sx={{ fontSize: 40, color: '#A8A8A8', height: '50px', width: '50px' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight={500}>
                      Upload XLSX File
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Browse file to upload
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
            <input
              type="file"
              accept=".xlsx"
              ref={fileInputRef}
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
          </Box>
        </Stack>

        {/* Submit Button */}
        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedFile}
          startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
        >
          REMOVE USERS
        </Button>
      </Box>
    );
  }
);

export default DeleteBulkUsersDrawerContent;
